import { AxiosResponse } from 'axios';
import { format, parse, startOfToday, startOfYesterday } from 'date-fns';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';

import {
  ComparisonGraph,
  ComparisonGraphFieldItem,
  ComparisonGraphParams,
  ComparisonGraphResponse,
  toComparisonGraphSetting,
} from '../domain/comparisonGraph';
import {
  normalizeComparisonGraphFieldsForFetch,
  resolveComparisonGraphFields,
  resolveComparisonGraphFieldsForSearch,
} from '../domain/comparisonGraphFields';
import { DataHallKiParams } from '../domain/dataHallKi';
import { GRAPH_DATE_TYPE } from '../domain/dataKiGraph';
import { ShuOption } from '../domain/shu';

import { dataHallKiDataSearchConditionSelector } from '../redux/server/dataHallKi';
import {
  ChangeDataHallKiGraphDateType,
  ChangeDataHallKiGraphField,
  DataHallKiGraphActionCreators,
  DataHallKiGraphActionTypes,
  FetchDataHallKiGraphAction,
  SearchDataHallKiGraph,
  SetDataHallKiGraphFieldsAction,
  dataHallKiGraphSearchConditionSelector,
} from '../redux/server/dataHallKiGraph';
import { Api, buildConfig } from '../utils/api';
import {
  calcEndDateWithExcludeToday,
  calcStartDateFromDateType,
} from '../utils/date';
import { handleErrorSaga } from './errorSaga';

type GraphFieldFetchSuccess = {
  field: string;
  response: ComparisonGraphResponse;
};

type GraphFieldFetchFailure = {
  field?: string;
  error: unknown;
};

type GraphFieldFetchResult = GraphFieldFetchSuccess | GraphFieldFetchFailure;

const isGraphFieldFetchSuccess = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchSuccess => 'response' in result;

const isGraphFieldFetchFailure = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchFailure => 'error' in result;

const toGraphApiParams = (
  params: ComparisonGraphParams,
  field: string
): ComparisonGraphParams => {
  const { field: _field, fields: _fields, ...commonParams } = params;
  return { ...commonParams, field };
};

const buildComparisonGraph = (
  results: GraphFieldFetchSuccess[]
): ComparisonGraph => {
  const firstResponse = results.at(0)?.response;
  if (!firstResponse) {
    throw new Error('Hall ki graph API response is empty');
  }
  const graphList: ComparisonGraphFieldItem[] = results.map(
    ({ field, response }) => ({
      field,
      data: response.data,
    })
  );
  return {
    setting: toComparisonGraphSetting(firstResponse.setting),
    graphList,
  };
};

function* fetchGraphByField(
  api: Api,
  params: ComparisonGraphParams,
  field: string
) {
  const response: AxiosResponse<ComparisonGraphResponse> = yield call(
    api.get,
    '/data/hall/ki/graph',
    buildConfig(toGraphApiParams(params, field))
  );
  return response.data;
}

function* fetchGraphByFieldSafely(
  api: Api,
  params: ComparisonGraphParams,
  field: string
): Generator<unknown, GraphFieldFetchResult, ComparisonGraphResponse> {
  try {
    const response = yield call(fetchGraphByField, api, params, field);
    return { field, response };
  } catch (error: unknown) {
    return { field, error };
  }
}

function* hideDataHallKiGraphOnFailure(shuCode: string) {
  yield put(DataHallKiGraphActionCreators.hideDataHallKiGraphAction(shuCode));
}

/**
 * 検索条件を元に推移グラフ（機種比較）のデータを取得する
 */
export function* fetchDataHallKiGraphSaga(
  api: Api,
  action: FetchDataHallKiGraphAction
) {
  const { shuCode, params } = action.payload;
  const fields = resolveComparisonGraphFields(params);

  yield put(
    DataHallKiGraphActionCreators.fetchDataHallKiGraphRequestAction(shuCode)
  );

  const results: GraphFieldFetchResult[] = yield all(
    fields.map((field) => call(fetchGraphByFieldSafely, api, params, field))
  );
  const successfulResults = results.filter(isGraphFieldFetchSuccess);
  const failedResults = results.filter(isGraphFieldFetchFailure);

  if (successfulResults.length === 0) {
    yield call(hideDataHallKiGraphOnFailure, shuCode);
    const firstError = failedResults.at(0)?.error;
    if (firstError !== undefined) {
      yield fork(handleErrorSaga, firstError);
    }
    return;
  }

  try {
    yield put(
      DataHallKiGraphActionCreators.fetchDataHallKiGraphSuccessAction(
        shuCode,
        buildComparisonGraph(successfulResults)
      )
    );
  } catch (error: unknown) {
    yield call(hideDataHallKiGraphOnFailure, shuCode);
    yield fork(handleErrorSaga, error);
    return;
  }

  if (failedResults.length > 0) {
    console.warn(
      `[dataHallKiGraphSaga] 一部グラフ項目の取得に失敗しました (${shuCode}): ${failedResults
        .map((result) => result.field ?? 'unknown')
        .join(', ')}`
    );
  }
}

function* refetchDataHallKiGraphWithFields(shu: ShuOption, fields: string[]) {
  const searchCondition: ComparisonGraphParams = yield select(
    dataHallKiGraphSearchConditionSelector(shu)
  );
  if (Object.keys(searchCondition).length === 0) {
    console.warn(
      `[dataHallKiGraphSaga] グラフ検索条件が未設定のため再取得をスキップしました (${shu.code})`
    );
    return;
  }
  yield put(
    DataHallKiGraphActionCreators.fetchDataHallKiGraphAction(shu.code, {
      ...searchCondition,
      fields: normalizeComparisonGraphFieldsForFetch(fields),
    })
  );
}

export function* searchDataHallKiGraphSaga(action: SearchDataHallKiGraph) {
  const { shu, kiList } = action.payload;

  const prevParams: ComparisonGraphParams = yield select(
    dataHallKiGraphSearchConditionSelector(shu)
  );
  const fields = resolveComparisonGraphFieldsForSearch(prevParams);
  const { dateType } = prevParams;

  const searchCondition: DataHallKiParams = yield select(
    dataHallKiDataSearchConditionSelector(shu)
  );

  const endDate = calcEndDateWithExcludeToday(
    !!searchCondition.excludeToday,
    searchCondition.ymdList && searchCondition.ymdList.slice(-1)[0]
  );
  const startDate = calcStartDateFromDateType(
    dateType ?? GRAPH_DATE_TYPE.DAILY,
    endDate
  );

  yield put(
    DataHallKiGraphActionCreators.fetchDataHallKiGraphAction(shu.code, {
      kiList,
      shuList: searchCondition.shuList,
      shuGroupIds: searchCondition.shuGroupIds,
      halls: searchCondition.halls,
      excludeToday: searchCondition.excludeToday,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      ...(dateType ? { dateType } : {}),
      fields,
    })
  );
}

function* changeDataHallKiGraphFieldSaga(action: ChangeDataHallKiGraphField) {
  const { shu, field } = action.payload;
  yield* refetchDataHallKiGraphWithFields(shu, [field]);
}

function* setDataHallKiGraphFieldsSaga(action: SetDataHallKiGraphFieldsAction) {
  const { shu, fields } = action.payload;
  yield* refetchDataHallKiGraphWithFields(shu, fields);
}

function* changeDataHallKiGraphDateTypeSaga(
  action: ChangeDataHallKiGraphDateType
) {
  const { shu, dateType } = action.payload;

  const searchCondition: ComparisonGraphParams = yield select(
    dataHallKiGraphSearchConditionSelector(shu)
  );
  const fields = resolveComparisonGraphFields(searchCondition);

  const searchEndDate =
    searchCondition.endDate === undefined
      ? startOfToday()
      : parse(searchCondition.endDate, 'yyyy-MM-dd', new Date());
  const endDate =
    searchCondition.excludeToday && searchEndDate >= startOfToday()
      ? startOfYesterday()
      : searchEndDate;
  const startDate = calcStartDateFromDateType(dateType, endDate);

  yield put(
    DataHallKiGraphActionCreators.fetchDataHallKiGraphAction(shu.code, {
      ...searchCondition,
      dateType,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      fields,
    })
  );
}

function* handleFetchDataHallKiGraphSaga(api: Api) {
  yield takeEvery(
    DataHallKiGraphActionTypes.FETCH_DATA_HALL_KI_GRAPH,
    fetchDataHallKiGraphSaga,
    api
  );
}

function* handleSearchDataHallKiGraphSaga() {
  yield takeEvery(
    DataHallKiGraphActionTypes.SEARCH_DATA_HALL_KI_GRAPH,
    searchDataHallKiGraphSaga
  );
  yield takeEvery(
    DataHallKiGraphActionTypes.CHANGE_DATA_HALL_KI_GRAPH_FIELD,
    changeDataHallKiGraphFieldSaga
  );
  yield takeEvery(
    DataHallKiGraphActionTypes.CHANGE_DATA_HALL_KI_GRAPH_DATE_TYPE,
    changeDataHallKiGraphDateTypeSaga
  );
  yield takeEvery(
    DataHallKiGraphActionTypes.SET_DATA_HALL_KI_GRAPH_FIELDS,
    setDataHallKiGraphFieldsSaga
  );
}

export function* dataHallKiGraphSaga(context: { api: Api }) {
  yield fork(handleFetchDataHallKiGraphSaga, context.api);
  yield fork(handleSearchDataHallKiGraphSaga);
}
